import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getVendorProducts,
  updatePickedQuantity,
  downloadDeliveryPDF,
  startDropOff,
  rejectAssignedDeliveries,
} from "@/store/slices/newDeliverySlice";

import {
  ArrowLeftIcon,
  PhoneIcon,
  MapPinIcon,
  PrinterIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { ArrowUpTrayIcon } from "@heroicons/react/24/solid";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import ShowLoader from "@/components/Loader";
import Toaster from "@/components/Toaster";
import OrderDeclineModal from "../modals/order-decline";
import DocumentViewModal from "@/components/DocumentViewModal";
import { useTranslation } from "react-i18next";

const GREEN = "#065C36";
const PAGE_BG = "#F9F9F9";
const ACCENT_YELLOW = "#FFB800";

export default function CollectProduct() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const { t } = useTranslation("delivery");

  const [products, setProducts] = useState([]);

  const [selectedForCancel, setSelectedForCancel] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);

  const {
    vendor_products = [],
    vendor_products_loading,
  } = useSelector((state) => state.delivery);

  const { order_id, vendor_id } = useParams();

  useEffect(() => {
    if (order_id && vendor_id) {
      dispatch(getVendorProducts({ order_id, vendor_id }));
    }
  }, [order_id, vendor_id, dispatch]);

  useEffect(() => {
    if (vendor_products.length > 0) {
      const updated = vendor_products.map((item) => ({
        ...item,
        id: item.delivery_assigned_order_id,
        collected_quantity: item.fully_picked
          ? (item.collected_quantity || 0)
          : (item.quantity || 0),
        actual_collected_quantity: item.collected_quantity || 0,
        fully_picked: item.fully_picked || false,
        is_actually_collected: (item.collected_quantity || 0) > 0,
        is_dropoff_start: item.is_dropoff_start || false,
        delivery_proof_uploaded: item.delivery_proof_uploaded || false,
      }));

      setProducts(updated);
    }
  }, [vendor_products]);

  const handleMarkAsCollectedSingle = (index) => {
    const item = products[index];

    const payload = [
      {
        id: item.id,
        collected_quantity: item.collected_quantity,
      },
    ];

    dispatch(updatePickedQuantity(payload))
      .unwrap()
      .then((responseData) => {
        Toaster.success(
          responseData?.message ||
          t("picked_quantity_updated_successfully")
        );

        dispatch(getVendorProducts({ order_id, vendor_id }));
      })
      .catch((error) => {
        Toaster.error(
          error?.message || t("failed_to_update_quantity")
        );
      });
  };

  const handleStartDropOff = (item) => {
    const payload = [item.id];

    dispatch(startDropOff(payload))
      .unwrap()
      .then((responseData) => {
        Toaster.success(
          responseData?.message || t("order_is_out_for_delivery")
        );

        dispatch(getVendorProducts({ order_id, vendor_id }));
      })
      .catch((error) => {
        Toaster.error(
          error?.message || t("failed_to_start_drop_off")
        );
      });
  };

  const handleUploadProof = (item) => {
    navigate(`/delivery-partner/proof-upload/${item.id}`);
  };

  const handlePrint = () => {
    const ids = products.map(
      (item) => item.delivery_assigned_order_id
    );

    if (ids.length === 0) {
      Toaster.error(t("no_products_available_to_print"));
      return;
    }

    dispatch(downloadDeliveryPDF(ids)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        const pdfUrl = res.payload?.data || res.payload?.url;

        if (pdfUrl) {
          window.open(pdfUrl, "_blank");
          Toaster.success(t("pdf_generated_successfully"));
        }
      }
    });
  };

  const handleMarkAllAsCollected = () => {
    const itemsToPick = products.filter(
      (item) => !item.fully_picked
    );

    if (itemsToPick.length === 0) {
      Toaster.info(t("all_items_already_marked_as_collected"));
      return;
    }

    // NEW
    if (itemsToPick.some((item) => !item.delivery_proof_uploaded)) {
      Toaster.error(t("please_upload_pickup_proof_first"));
      return;
    }

    const payload = itemsToPick.map((item) => ({
      id: item.id,
      // collected_quantity: item.assigned_quantity,
      collected_quantity: item.quantity,
    }));

    dispatch(updatePickedQuantity(payload))
      .unwrap()
      .then((responseData) => {
        Toaster.success(
          responseData?.message ||
          t("all_items_marked_as_collected")
        );

        dispatch(getVendorProducts({ order_id, vendor_id }));
      })
      .catch((error) => {
        Toaster.error(
          error?.message || t("failed_to_update_items")
        );
      });
  };

  const handleQuantityChange = (index, delta) => {
    const updatedProducts = [...products];
    const item = updatedProducts[index];

    const newValue =
      (item.collected_quantity || 0) + delta;

    if (newValue >= 0 && newValue <= item.quantity) {
      updatedProducts[index].collected_quantity = newValue;
      setProducts(updatedProducts);
    }
  };

  const handleManualInput = (index, value) => {
    const updatedProducts = [...products];
    const item = updatedProducts[index];
    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) {
      updatedProducts[index].collected_quantity = 0;
    } else if (numValue <= item.quantity) {
      updatedProducts[index].collected_quantity = numValue;
    } else {
      updatedProducts[index].collected_quantity = item.quantity;
    }

    setProducts(updatedProducts);
  };

  const selectableIds = products
    .filter((p) => p.actual_collected_quantity === 0)
    .map((p) => p.id);

  const isAllSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) =>
      selectedForCancel.includes(id)
    );

  const isIndeterminate =
    selectedForCancel.length > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    const selectableIds = products
      .filter((p) => p.actual_collected_quantity === 0)
      .map((p) => p.id);

    if (isAllSelected) {
      setSelectedForCancel([]);
    } else {
      setSelectedForCancel(selectableIds);
    }
  };

  const toggleCancelSelection = (itemId) => {
    setSelectedForCancel((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleOpenCancelModal = () => {
    if (selectedForCancel.length === 0) {
      Toaster.error(t("please_select_at_least_one_product_to_cancel"));
      return;
    }

    setShowCancelModal(true);
  };

  const handleConfirmCancel = (reason) => {
    setShowCancelModal(false);

    const isCancellingAllProducts =
      selectedForCancel.length === products.length;

    dispatch(
      rejectAssignedDeliveries({
        delivery_assigned_order_ids: selectedForCancel,
        description: reason,
      })
    )
      .unwrap()
      .then((responseData) => {
        Toaster.success(
          responseData?.message ||
          t("delivery_cancelled_successfully")
        );

        setSelectedForCancel([]);

        if (isCancellingAllProducts) {
          navigate(-2);
        } else {
          dispatch(
            getVendorProducts({ order_id, vendor_id })
          );
        }
      })
      .catch((error) => {
        Toaster.error(
          error?.message ||
          t("failed_to_cancel_delivery")
        );
      });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F4F2]">
      {vendor_products_loading && products.length === 0 && (
        <ShowLoader />
      )}

      <header className="bg-[#065C36] text-white shrink-0">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-8 pb-6 sm:pt-12 sm:pb-8">
          <div className="flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)_minmax(0,1.1fr)] md:items-start md:gap-x-4">
            <div className="flex items-start justify-between gap-3 md:contents">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-xs sm:text-sm text-left hover:opacity-90 min-w-0 md:col-start-1 md:row-start-1 -mt-2 sm:-mt-6"
              >
                <ArrowLeftIcon className="w-4 h-4 shrink-0" />

                <span className="leading-snug">
                  <span className="hidden sm:inline">
                    {t("back_to_assigned_order_vendors_details")}
                  </span>

                  <span className="sm:hidden">
                    {t("back")}
                  </span>
                </span>
              </button>
            </div>

            <div className="text-center md:col-span-3 md:px-4 space-y-2 mt-2 relative">
              <h1 className="text-lg sm:text-xl font-semibold uppercase">
                {state?.company_name || ""}
              </h1>

              <div className="flex justify-center items-center gap-2 text-sm text-green-100">
                <PhoneIcon className="w-4 h-4 shrink-0 text-white" />
                <span>{state?.phone_number || ""}</span>
              </div>

              <div className="flex justify-center items-start gap-2 text-sm text-green-100 max-w-2xl mx-auto">
                <MapPinIcon className="w-4 h-4 shrink-0 mt-0.5 text-white" />

                <span className="text-center break-words">
                  {state?.pickup_location || ""}
                </span>
              </div>

              <div className="absolute right-0 top-1/2 -translate-y-1/2 flex justify-end md:col-start-3 md:row-start-1 md:justify-self-end">
                <button
                  type="button"
                  aria-label={t("open_in_google_maps")}
                  className="shrink-0 bg-[#F4B11F] w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:brightness-105"
                  onClick={() => {
                    const lat = state?.latitude;
                    const lng = state?.longitude;
                    const address = state?.pickup_landmark;

                    if (lat != null && lng != null) {
                      window.open(
                        `https://www.google.com/maps?q=${lat},${lng}`,
                        "_blank"
                      );
                    } else if (address) {
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          address
                        )}`,
                        "_blank"
                      );
                    }
                  }}
                >
                  <img
                    src="/img/icons/navigate.png"
                    alt={t("navigate")}
                    className="w-8 h-8 object-contain"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {products.length > 0 && (
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-5">
          <div className="bg-white border border-red-100 rounded-xl px-4 py-2.5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 pr-3 border-r border-gray-200">
                  <input
                    type="checkbox"
                    id="select-all-cancel"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = isIndeterminate;
                      }
                    }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 focus:ring-offset-0 focus:ring-0 cursor-pointer"
                    style={{ accentColor: "#065C36" }}
                  />

                  <label
                    htmlFor="select-all-cancel"
                    className="text-xs text-gray-600 font-bold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                  >
                    {t("select_all")}
                  </label>
                </div>

                <p className="text-sm text-gray-400 italic font-medium hidden sm:block">
                  {t("having_issue_delivering_order")}
                </p>
              </div>

              <div className="flex items-center gap-3 ml-auto">
                {selectedForCancel.length > 0 && (
                  <span className="text-xs text-red-500 font-bold animate-fade-in">
                    {selectedForCancel.length} {t("selected")}
                  </span>
                )}

                <button
                  type="button"
                  onClick={handleOpenCancelModal}
                  disabled={selectedForCancel.length === 0}
                  className={`shrink-0 flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg border transition-all ${selectedForCancel.length > 0
                    ? "border-red-500 text-red-500 hover:bg-red-50 shadow-sm"
                    : "border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50"
                    }`}
                >
                  {t("cancel_delivery")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCTS GRID */}
      <div className="max-w-7xl mx-auto w-full py-6 sm:py-10 px-4 sm:px-6 flex-1 pb-28">
        {vendor_products_loading && products.length === 0 ? null : products.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            {t("no_products_found")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {products.map((item, index) => (
              <div
                key={
                  item.id ??
                  item.delivery_assigned_order_id ??
                  index
                }
                className="bg-white rounded-xl p-5 sm:p-6 border border-gray-100 transition-all shadow-sm flex flex-col h-full"
              >
                <div className="flex items-center justify-end mb-2 -mt-1">
                  <label
                    className={`flex items-center gap-1.5 select-none group ${item.actual_collected_quantity > 0
                      ? "cursor-not-allowed opacity-40"
                      : "cursor-pointer"
                      }`}
                  >
                    <span
                      className={`text-[11px] italic transition-colors ${item.actual_collected_quantity > 0
                        ? "text-gray-400"
                        : "text-gray-400 group-hover:text-red-400"
                        }`}
                    >
                      {item.actual_collected_quantity > 0
                        ? t("already_picked")
                        : t("select_to_cancel")}
                    </span>

                    <input
                      type="checkbox"
                      checked={selectedForCancel.includes(item.id)}
                      onChange={() =>
                        toggleCancelSelection(item.id)
                      }
                      disabled={
                        item.actual_collected_quantity > 0
                      }
                      className="w-4 h-4 rounded border-gray-300 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ accentColor: "#065C36" }}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-24 h-24 rounded-lg flex items-center justify-center p-2">
                      <img
                        src={
                          item.product_image ||
                          "/img/photo-gallery.png"
                        }
                        alt={
                          item.product_name ||
                          t("product")
                        }
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>

                    <div className="mt-1 flex-1">
                      <h3 className="font-bold text-[#1F2937] text-lg leading-tight line-clamp-2">
                        {item.product_name}
                      </h3>

                      <p className="text-xs font-bold text-[#1F2937] mt-1 uppercase tracking-widest">
                        {item.sku_number}
                      </p>

                      {item.purchase_invoice_file && (
                        <button
                          type="button"
                          onClick={() =>
                            setInvoicePreview(
                              item.purchase_invoice_file
                            )
                          }
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#065C36] hover:underline"
                        >
                          <EyeIcon className="w-4 h-4" />

                          {t("view_purchase_invoice")}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <img
                        src="/img/icons/Containers.png"
                        className="w-6 h-8 opacity-70"
                        alt=""
                      />

                      <span className="text-sm text-gray-600 font-semibold">
                        {t("type")}: {item.category_name}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <img
                        src={
                          item.is_installation
                            ? "/img/icons/Container-3.png"
                            : "/img/icons/Container.png"
                        }
                        className="w-5 h-7 opacity-70"
                        alt=""
                      />

                      <span className="text-sm text-gray-600 font-semibold">
                        {item.is_installation
                          ? t("delivery_and_installation")
                          : t("delivery")}
                      </span>
                    </div>

                    {/* ASSIGNED QUANTITY */}
                    <div className="flex items-center gap-3">
                      <img
                        src="/img/icons/self.png"
                        className="w-6 h-6 opacity-70"
                        alt=""
                      />

                      <span className="text-sm text-gray-600 font-semibold">
                        {t("assigned_quantity")}:{" "}
                        {item.assigned_quantity} {t("units")}
                      </span>
                    </div>

                    {/* REMAINING / REQUIRED QUANTITY */}
                    <div className="flex items-center gap-3">
                      <img
                        src="/img/icons/self.png"
                        className="w-6 h-6 opacity-70"
                        alt=""
                      />

                      <span className="text-sm text-gray-600 font-semibold">
                        {t("required_quantity")}:{" "}
                        {item.quantity} {t("units")}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-[#065C36] opacity-70">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="20"
                          fill="currentColor"
                          className="bi bi-cart"
                          viewBox="0 0 16 16"
                        >
                          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l1.313 7h8.17l1.313-7zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                        </svg>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 font-semibold">
                          {t("collected")}:
                        </span>

                        <div className="flex items-center border border-gray-300 rounded-md bg-white overflow-hidden h-8">
                          {/* MINUS BUTTON */}
                          <button
                            type="button"
                            onClick={() =>
                              handleQuantityChange(index, -1)
                            }
                            disabled={
                              item.fully_picked ||
                              item.collected_quantity <= 0
                            }
                            className="px-2 text-lg font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            -
                          </button>

                          <input
                            type="number"
                            value={item.collected_quantity}
                            onChange={(e) =>
                              handleManualInput(
                                index,
                                e.target.value
                              )
                            }
                            disabled={item.fully_picked}
                            className="w-10 text-center text-sm font-bold border-gray-300 bg-transparent focus:ring-0 p-0 text-[#065C36] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />

                          {/* PLUS BUTTON */}
                          <button
                            type="button"
                            onClick={() =>
                              handleQuantityChange(index, 1)
                            }
                            disabled={
                              item.fully_picked ||
                              item.collected_quantity >=
                              item.quantity
                            }
                            className="px-2 text-lg font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>

                        <span className="text-sm text-gray-500 font-medium">
                          {t("units")}
                        </span>
                      </div>
                    </div>

                    {item.fully_picked && (
                      <div className="flex flex-col gap-2 mt-1 pt-4 border-t border-gray-50">
                        <label className="flex items-center gap-3 cursor-default">
                          <input
                            type="checkbox"
                            checked={item.fully_picked}
                            readOnly
                            className="w-4 h-4 rounded border-gray-300 accent-[#F4B11F] focus:ring-0"
                          />

                          <span className="text-xs text-gray-600 font-medium">
                            {t("mark_all_as_collected")}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* <div className="mt-6 pt-5 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        !item.fully_picked &&
                        (item.collected_quantity || 0) > 0 &&
                        handleMarkAsCollectedSingle(index)
                      }
                      disabled={
                        item.fully_picked ||
                        (item.collected_quantity || 0) === 0
                      }
                      className={`py-2.5 rounded-lg text-xs font-bold transition-colors ${item.fully_picked ||
                          (item.collected_quantity || 0) === 0
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300"
                          : "bg-[#065C36] text-white hover:bg-[#044a2c]"
                        }`}
                    >
                      {item.fully_picked
                        ? t("collected")
                        : t("mark_as_collected")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleStartDropOff(item)
                      }
                      disabled={
                        !item.is_actually_collected ||
                        item.delivery_proof_uploaded
                      }
                      className={`py-2.5 rounded-lg text-xs font-bold transition-colors ${item.is_actually_collected &&
                          !item.delivery_proof_uploaded
                          ? "bg-[#F4B11F] text-white hover:bg-[#d99a1a]"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      {t("start_drop_off")}
                    </button>
                  </div>

                  {item.is_dropoff_start &&
                    !item?.delivery_proof_uploaded && (
                      <button
                        type="button"
                        onClick={() =>
                          handleUploadProof(item)
                        }
                        className="w-full bg-[#F4B11F] text-white py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#d99a1a] transition-colors"
                      >
                        <ArrowUpTrayIcon className="w-5 h-5" />

                        {t("upload_proof")}
                      </button>
                    )}
                </div> */}

                <div className="mt-6 pt-5 border-t border-gray-100 space-y-3">
                  {/* STEP 1: UPLOAD PICKUP PROOF */}
                  <button
                    type="button"
                    onClick={() => handleUploadProof(item)}
                    disabled={item.delivery_proof_uploaded || item.fully_picked}
                    className={`w-full py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors ${item.delivery_proof_uploaded || item.fully_picked
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-[#F4B11F] text-white hover:bg-[#d99a1a]"
                      }`}
                  >
                    <ArrowUpTrayIcon className="w-5 h-5" />
                    {item.delivery_proof_uploaded || item.fully_picked
                      ? t("proof_uploaded")
                      : t("upload_proof")}
                  </button>

                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {/* STEP 2: MARK AS COLLECTED */}
                    <button
                      type="button"
                      onClick={() =>
                        item.delivery_proof_uploaded &&
                        !item.fully_picked &&
                        (item.collected_quantity || 0) > 0 &&
                        handleMarkAsCollectedSingle(index)
                      }
                      disabled={
                        !item.delivery_proof_uploaded ||
                        item.fully_picked ||
                        (item.collected_quantity || 0) === 0
                      }
                      className={`py-2.5 rounded-lg text-xs font-bold transition-colors ${!item.delivery_proof_uploaded ||
                        item.fully_picked ||
                        (item.collected_quantity || 0) === 0
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300"
                        : "bg-[#065C36] text-white hover:bg-[#044a2c]"
                        }`}
                    >
                      {item.fully_picked ? t("collected") : t("mark_as_collected")}
                    </button>

                    {/* STEP 3: START DROP OFF */}
                    <button
                      type="button"
                      onClick={() => handleStartDropOff(item)}
                      disabled={
                        !item.is_actually_collected ||
                        (item.fully_picked && item.is_dropoff_start)
                      }
                      className={`py-2.5 rounded-lg text-xs font-bold transition-colors ${item.is_actually_collected &&
                        !(item.fully_picked && item.is_dropoff_start)
                        ? "bg-[#F4B11F] text-white hover:bg-[#d99a1a]"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      {t("start_drop_off")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <footer
        className="px-4 sm:px-6 lg:px-8 pt-5 pb-5 sm:pt-6 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))] sticky bottom-0 shadow-[0_-10px_40px_rgba(0,0,0,0.12)]"
        style={{ backgroundColor: GREEN }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* PRINT BUTTON */}
          <button
            type="button"
            onClick={handlePrint}
            className="w-full border border-white text-white py-3.5 rounded-xl flex items-center justify-center gap-2.5 font-medium text-base hover:bg-white/10 active:bg-white/[0.15] transition-colors"
          >
            <PrinterIcon className="w-5 h-5 shrink-0 stroke-[1.75]" />

            {t("print")}
          </button>

          <button
            type="button"
            onClick={handleMarkAllAsCollected}
            disabled={products.every(
              (p) => p.fully_picked
            )}
            className={`w-full py-3.5 rounded-xl flex items-center justify-center font-bold text-base transition-all shadow-md ${products.every((p) => p.fully_picked)
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-[#F2AD2C] hover:bg-[#9CA3AF] text-[#1F2937]"
              }`}
          >
            {t("mark_as_collected")}
          </button>
        </div>
      </footer>

      {invoicePreview && (
        <DocumentViewModal
          documentUrl={invoicePreview}
          setIsDocumentViewModalOpen={setInvoicePreview}
        />
      )}

      <OrderDeclineModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onDecline={(reason) =>
          handleConfirmCancel(reason)
        }
      />
    </div>
  );
}